// TODO(MRF-163): when this stabilizes, also wire into map-app's map mount.

export type MinimalMap = {
  on(type: string, handler: (e: unknown) => void): unknown;
  off(type: string, handler: (e: unknown) => void): unknown;
  isStyleLoaded?: () => boolean;
};

export type MapLoadWatchdogPhase = 'pending' | 'healthy' | 'warning' | 'dismissed';

export type MapLoadWatchdogSignal = 'timeout' | 'error_count' | 'webgl_unsupported' | 'context_lost';

export type MapLoadWatchdogState = {
  phase: MapLoadWatchdogPhase;
  signal?: MapLoadWatchdogSignal;
  errorCount: number;
  failedHosts: string[];
  mountedAtMs: number;
  shownAtMs?: number;
  webglSupported: boolean;
};

export type MapLoadWatchdogClock = {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

export type MapLoadWatchdogOptions = {
  timeoutMs?: number;
  errorThreshold?: number;
  clock?: MapLoadWatchdogClock;
  isWebglSupported?: () => boolean;
};

export type MapLoadWatchdog = {
  subscribe(listener: (state: MapLoadWatchdogState) => void): () => void;
  getState(): MapLoadWatchdogState;
  dismiss(): void;
  dispose(): void;
};

export type MapLoadWatchdogShownEvent = {
  name: 'mrf_map_load_warning_shown';
  properties: {
    signal: MapLoadWatchdogSignal;
    agencyId: string;
    agencyName: string;
    errorCount: number;
    failedHosts: string[];
    timeSinceMountMs: number;
    webglSupported: boolean;
    $insert_id: string;
  };
};

export type MapLoadWatchdogRecoveredEvent = {
  name: 'mrf_map_load_warning_recovered';
  properties: {
    signal: MapLoadWatchdogSignal | undefined;
    shownDurationMs: number;
  };
};

export type MapLoadWatchdogTelemetryEvent = MapLoadWatchdogShownEvent | MapLoadWatchdogRecoveredEvent;

export const TRACKED_HOSTS = [
  'api.mapbox.com',
  'events.mapbox.com',
  'mapserver.civilgrid.com',
  'mapserver-new.civilgrid.com',
] as const;

export const TRACKED_HOST_SUFFIXES = ['.tiles.mapbox.com'] as const;

export function isTrackedHost(hostname: string): boolean {
  if (!hostname) return false;
  const lower = hostname.toLowerCase();
  if ((TRACKED_HOSTS as readonly string[]).includes(lower)) return true;
  return TRACKED_HOST_SUFFIXES.some((suffix) => lower.endsWith(suffix) && lower.length > suffix.length);
}

export function extractHostname(url: unknown): string | null {
  if (typeof url !== 'string' || url.length === 0) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

// Probes WebGL2 specifically — mapbox-gl v3 dropped WebGL1 support. Fail-open: probe-throws
// return true so we don't show a scary overlay because the probe itself blew up; the
// consumer's React error boundary handles real construction failures.
export function probeWebGL2(): boolean {
  if (typeof document === 'undefined') return true;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    return !!gl && !gl.isContextLost();
  } catch {
    return true;
  }
}

export type MapLoadWatchdogContext = {
  agencyId: string;
  agencyName: string;
  nowMs: number;
};

const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_ERROR_THRESHOLD = 3;

const realClock: MapLoadWatchdogClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

function isStyleAlreadyLoaded(map: MinimalMap): boolean {
  if (typeof map.isStyleLoaded !== 'function') return false;
  try {
    return map.isStyleLoaded();
  } catch {
    return false;
  }
}

export function createMapLoadWatchdog(map: MinimalMap, options: MapLoadWatchdogOptions = {}): MapLoadWatchdog {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const errorThreshold = options.errorThreshold ?? DEFAULT_ERROR_THRESHOLD;
  const clock = options.clock ?? realClock;

  // Fail-open: a missing or throwing probe assumes supported.
  const webglSupported = (() => {
    if (!options.isWebglSupported) return true;
    try {
      return options.isWebglSupported();
    } catch {
      return true;
    }
  })();

  const state: MapLoadWatchdogState = {
    phase: 'pending',
    errorCount: 0,
    failedHosts: [],
    mountedAtMs: clock.now(),
    webglSupported,
  };

  // `reuseMaps` can hand us an already-loaded map where style.load won't re-fire.
  let styleLoaded = isStyleAlreadyLoaded(map);
  let firstIdleSeen = false;
  let contextLost = false;
  let timerExpired = false;
  let disposed = false;
  let timerHandle: unknown = null;
  const listeners = new Set<(state: MapLoadWatchdogState) => void>();

  function snapshot(): MapLoadWatchdogState {
    return { ...state, failedHosts: [...state.failedHosts] };
  }

  function emit() {
    const snap = snapshot();
    // Copy so a listener that unsubscribes or re-enters emit doesn't perturb iteration.
    for (const listener of [...listeners]) {
      try {
        listener(snap);
      } catch {
        // A throwing listener must not poison the watchdog.
      }
    }
  }

  function transitionToWarning(nextSignal: MapLoadWatchdogSignal) {
    const wasWarning = state.phase === 'warning';
    state.phase = 'warning';
    state.signal = nextSignal;
    if (!wasWarning) {
      state.shownAtMs = clock.now();
    }
    emit();
  }

  function recompute() {
    if (disposed) return;
    if (state.phase === 'dismissed') return;

    if (!webglSupported) {
      if (state.signal !== 'webgl_unsupported') transitionToWarning('webgl_unsupported');
      return;
    }

    if (contextLost) {
      if (state.signal !== 'context_lost') transitionToWarning('context_lost');
      return;
    }

    if (!styleLoaded && timerExpired) {
      if (state.signal !== 'timeout') transitionToWarning('timeout');
      return;
    }

    if (!firstIdleSeen && state.errorCount >= errorThreshold) {
      if (state.signal !== 'error_count') transitionToWarning('error_count');
      return;
    }

    if (state.phase === 'warning') {
      const recovered =
        (state.signal === 'timeout' && styleLoaded) ||
        (state.signal === 'error_count' && firstIdleSeen) ||
        (state.signal === 'context_lost' && !contextLost);
      if (recovered) {
        state.phase = 'healthy';
        state.signal = undefined;
        emit();
      }
      return;
    }

    if (styleLoaded || timerExpired) {
      if (state.phase !== 'healthy') {
        state.phase = 'healthy';
        emit();
      }
    }
  }

  const onError = (e: unknown) => {
    if (disposed || firstIdleSeen) return;
    const err = (e as { error?: unknown }).error;
    const candidate =
      (err as { url?: unknown } | undefined)?.url ??
      (e as { url?: unknown }).url ??
      (err as { target?: { responseURL?: unknown } } | undefined)?.target?.responseURL;
    const host = extractHostname(candidate);
    if (!host || !isTrackedHost(host)) return;
    state.errorCount += 1;
    if (!state.failedHosts.includes(host)) state.failedHosts.push(host);
    recompute();
  };
  const onStyleLoad = () => {
    styleLoaded = true;
    recompute();
  };
  const onIdle = () => {
    // Mapbox idle guarantees style/data settled; treat it as style loaded.
    styleLoaded = true;
    firstIdleSeen = true;
    recompute();
  };
  const onContextLost = () => {
    contextLost = true;
    recompute();
  };
  const onContextRestored = () => {
    contextLost = false;
    recompute();
  };

  map.on('error', onError);
  map.on('style.load', onStyleLoad);
  map.on('idle', onIdle);
  map.on('webglcontextlost', onContextLost);
  map.on('webglcontextrestored', onContextRestored);

  timerHandle = clock.setTimeout(() => {
    timerExpired = true;
    recompute();
  }, timeoutMs);

  // Healthy immediately when mounted onto a reused map that already finished style loading.
  if (styleLoaded) recompute();

  // Surface webgl_unsupported immediately; sticky, no recovery path.
  if (!webglSupported) recompute();

  return {
    subscribe(listener) {
      listeners.add(listener);
      try {
        listener(snapshot());
      } catch {
        // See emit().
      }
      return () => {
        listeners.delete(listener);
      };
    },
    getState() {
      return snapshot();
    },
    dismiss() {
      if (disposed || state.phase === 'dismissed') return;
      state.phase = 'dismissed';
      // Keep state.signal so consumers can attribute the dismissal to a signal.
      emit();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (timerHandle != null) clock.clearTimeout(timerHandle);
      timerHandle = null;
      map.off('error', onError);
      map.off('style.load', onStyleLoad);
      map.off('idle', onIdle);
      map.off('webglcontextlost', onContextLost);
      map.off('webglcontextrestored', onContextRestored);
      listeners.clear();
    },
  };
}

// Watchdog with no real map, permanently in webgl_unsupported. Use when probeWebGL2 returned
// false or the consumer's error boundary caught a sync Map constructor throw.
export function createDisabledMapLoadWatchdog(options: MapLoadWatchdogOptions = {}): MapLoadWatchdog {
  const noopMap: MinimalMap = { on() {}, off() {} };
  return createMapLoadWatchdog(noopMap, { ...options, isWebglSupported: () => false });
}

export function diffMapLoadWatchdogStates(
  prev: MapLoadWatchdogState,
  next: MapLoadWatchdogState,
  ctx: MapLoadWatchdogContext,
): MapLoadWatchdogTelemetryEvent[] {
  const events: MapLoadWatchdogTelemetryEvent[] = [];

  // Per-episode dedup keyed on shownAtMs — stable within a warning, changes on re-entry.
  if (
    next.phase === 'warning' &&
    typeof next.shownAtMs === 'number' &&
    next.signal !== undefined &&
    next.shownAtMs !== prev.shownAtMs
  ) {
    events.push({
      name: 'mrf_map_load_warning_shown',
      properties: {
        signal: next.signal,
        agencyId: ctx.agencyId,
        agencyName: ctx.agencyName,
        errorCount: next.errorCount,
        failedHosts: [...next.failedHosts],
        timeSinceMountMs: next.shownAtMs - next.mountedAtMs,
        webglSupported: next.webglSupported,
        $insert_id: `${next.shownAtMs}-${next.signal}`,
      },
    });
  }

  // Auto-heal only; dismiss/CTA paths fire imperatively from the overlay.
  if (prev.phase === 'warning' && next.phase === 'healthy' && typeof prev.shownAtMs === 'number') {
    events.push({
      name: 'mrf_map_load_warning_recovered',
      properties: {
        signal: prev.signal,
        shownDurationMs: ctx.nowMs - prev.shownAtMs,
      },
    });
  }

  return events;
}
